import { useState } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import type { PublicUser } from '@sirocco/shared';
import { users as usersApi } from '../../api/endpoints';
import { SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState } from '../../components/ui/Bits';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { t } from '../../i18n';
import { etatDuDossier, GestesDuMembre, useGestesDuMembre } from './Gestes';

/**
 * L'administration, depuis le telephone.
 *
 * La file de moderation traite des contenus signales ; cet ecran-ci
 * traite des personnes, et se prend par le pseudo plutot que par un
 * signalement. C'est ce qui manquait : nommer un moderateur ou effacer un
 * compte demandait jusqu'ici un script et un ordinateur.
 *
 * Les gestes eux-memes vivent dans `Gestes.tsx`, que le profil d'un
 * membre partage : l'administrateur y trouve les memes, sans chercher.
 */
export function AdminScreen() {
  const [recherche, setRecherche] = useState('');
  const [trouves, setTrouves] = useState<PublicUser[] | null>(null);
  const [cherche, setCherche] = useState(false);
  const [membre, setMembre] = useState<PublicUser | null>(null);

  const outils = useGestesDuMembre(membre, {
    apresSuppression: () => {
      setMembre(null);
      setTrouves(null);
    },
  });

  async function chercher() {
    const q = recherche.trim();
    if (q.length < 2) return;
    setCherche(true);
    setTrouves(await usersApi.search(q).catch(() => []));
    setCherche(false);
  }

  return (
    <SubScreen title={t.moderation.admin.titre} overlay={outils.fenetre}>
      <div className="adm">
        <form
          className="adm__quete"
          onSubmit={(e) => {
            e.preventDefault();
            void chercher();
          }}
        >
          <Field
            label={t.moderation.admin.chercherMembre}
            placeholder={t.moderation.admin.sonPseudo}
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
          />
          <Button type="submit" icon={<MagnifyingGlass size={18} />} disabled={cherche}>
            {t.moderation.admin.chercher}
          </Button>
        </form>

        {trouves?.length === 0 && <EmptyState title={t.moderation.admin.personne} />}

        {trouves && trouves.length > 0 && !membre && (
          <ul className="adm__liste">
            {trouves.map((u) => (
              <li key={u.id}>
                <button className="adm__ligne" onClick={() => setMembre(u)}>
                  <Avatar user={u} size={40} />
                  <span className="adm__pseudo">{u.username}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {membre && (
          <div className="adm__fiche">
            <button className="adm__retour" onClick={() => setMembre(null)}>
              {t.moderation.admin.autre}
            </button>

            <div className="adm__tete">
              <Avatar user={membre} size={56} />
              <div>
                <p className="adm__pseudo">{membre.username}</p>
                {outils.dossier && <p className="adm__etat">{etatDuDossier(outils.dossier)}</p>}
              </div>
            </div>

            <GestesDuMembre outils={outils} />
          </div>
        )}
      </div>
    </SubScreen>
  );
}
